import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, endpoints } from "@/lib/api";
import { loadRazorpayScript, openRazorpayCheckout } from "@/lib/payments";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CheckCircle2, Copy, ShieldCheck, Landmark, CreditCard, Smartphone, Banknote, Lock, FlaskConical } from "lucide-react";

const PRESETS = [500, 1000, 2500, 5000, 10000, 25000];
const ONLINE_METHODS = ["upi", "credit_card", "debit_card", "net_banking"];

export const DonateForm = () => {
  const navigate = useNavigate();
  const [method, setMethod] = useState("upi");
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", amount: 1000, currency: "INR", purpose: "General Fund", pan: "", address: "", anonymous: false, note: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [payConfig, setPayConfig] = useState({ mode: "test" });
  const [simulator, setSimulator] = useState(null); // { donation, order }
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    api.get(endpoints.paymentsConfig).then(setPayConfig).catch(() => {});
    api.get(endpoints.campaigns).then((list) => setCampaigns((Array.isArray(list) ? list : []).filter((c) => c.status !== "completed"))).catch(() => {});
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const isOnline = ONLINE_METHODS.includes(method);

  const finishPayment = async (donationId, orderId, paymentId, signature) => {
    try {
      await api.post(endpoints.paymentsVerify, {
        donation_id: donationId,
        razorpay_order_id: orderId,
        razorpay_payment_id: paymentId,
        razorpay_signature: signature || null,
      });
      toast.success("Payment successful! Your 80G receipt is ready.");
      navigate(`/receipt/${donationId}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment verification failed. Please contact us.");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, amount: Number(form.amount), method };
      const donation = await api.post(endpoints.donations, payload);

      if (!isOnline) {
        // Bank transfer stays a recorded intent with manual instructions
        setDone(donation);
        toast.success("Thank you. Complete the bank transfer using the details shown — we'll confirm within 24 hours.");
        return;
      }

      const order = await api.post(endpoints.paymentsOrder, { donation_id: donation.id });

      if (order.mode === "live") {
        const ok = await loadRazorpayScript();
        if (!ok) { toast.error("Could not load the payment gateway. Please try again."); return; }
        openRazorpayCheckout(order, {
          onSuccess: (res) => finishPayment(donation.id, res.razorpay_order_id, res.razorpay_payment_id, res.razorpay_signature),
          onDismiss: () => toast.info("Payment cancelled. Your donation is saved — you can complete it anytime."),
        });
      } else {
        // TEST MODE simulator — identical flow, clearly labeled
        setSimulator({ donation, order });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your inputs.");
    } finally { setLoading(false); }
  };

  if (done) return (
    <div data-testid="donate-form-success" className="rounded-xl border border-[hsl(var(--green)/0.25)] bg-[hsl(var(--surface-tint-green))] p-8">
      <CheckCircle2 className="h-10 w-10 text-[hsl(var(--green))]" />
      <h3 className="heading-3 mt-3">Thank you, {done.full_name}.</h3>
      <p className="lead mt-2">Your donation of <strong>₹{done.amount}</strong> via <strong>bank transfer</strong> for <em>{done.purpose}</em> is recorded (ref <code className="text-xs">{done.id.slice(0,8)}</code>).</p>
      <div className="mt-4 rounded-lg bg-card border border-[hsl(var(--border))] p-4 text-sm">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><div className="eyebrow mb-0.5">Account name</div>Citizen Awareness Foundation</div>
          <div><div className="eyebrow mb-0.5">Account number</div><span className="tabular">50100 22233 44455</span></div>
          <div><div className="eyebrow mb-0.5">IFSC</div>HDFC0000123</div>
          <div><div className="eyebrow mb-0.5">Bank</div>HDFC Bank, MG Road, Bengaluru</div>
        </div>
      </div>
      <p className="text-xs text-[hsl(var(--muted-ink))] mt-4">Reply to our confirmation email with your transaction reference and we'll issue your 80G receipt.</p>
    </div>
  );

  return (
    <>
    <form onSubmit={onSubmit} className="grid gap-6" data-testid="donate-form">
      <div>
        <Label className="text-sm font-medium text-[hsl(var(--navy))]">Choose an amount (INR)</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button type="button" key={p} data-testid={`donate-preset-${p}`} onClick={() => set("amount")(p)} className={`px-4 h-10 rounded-full text-sm border transition-colors ${Number(form.amount) === p ? "border-[hsl(var(--navy))] bg-[hsl(var(--surface-tint))] text-[hsl(var(--navy))] font-semibold" : "border-[hsl(var(--border))] text-[hsl(var(--muted-ink))] hover:border-[hsl(var(--navy))]"}`}>₹{p.toLocaleString("en-IN")}</button>
          ))}
          <Input data-testid="donate-amount" type="number" min="100" className="w-32" value={form.amount} onChange={set("amount")} />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Full name*"><Input required data-testid="donate-full_name" value={form.full_name} onChange={set("full_name")} /></Field>
        <Field label="Email*"><Input required type="email" data-testid="donate-email" value={form.email} onChange={set("email")} /></Field>
        <Field label="Phone"><Input data-testid="donate-phone" value={form.phone} onChange={set("phone")} /></Field>
        <Field label="Purpose">
          <Select value={form.purpose} onValueChange={set("purpose")}>
            <SelectTrigger data-testid="donate-purpose"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["General Fund","Disaster Relief","Education","Health & Medical Camps","Environment","Women & Youth Empowerment"].map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
              {campaigns.length > 0 && campaigns.map((c) => (<SelectItem key={c.id} value={c.title}>Campaign: {c.title}</SelectItem>))}
            </SelectContent>
          </Select>
          {campaigns.some((c) => c.title === form.purpose) && (
            <p className="text-xs text-[hsl(var(--green))] mt-1.5" data-testid="donate-campaign-live-note">Your donation will instantly grow this campaign's raised total.</p>
          )}
        </Field>
        <Field label="PAN (for 80G receipt)"><Input data-testid="donate-pan" value={form.pan} onChange={set("pan")} placeholder="AAAAA9999A" /></Field>
        <Field label="Address"><Input data-testid="donate-address" value={form.address} onChange={set("address")} /></Field>
      </div>

      <div>
        <Label className="text-sm font-medium text-[hsl(var(--navy))]">Payment method</Label>
        {payConfig.mode === "test" && (
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--orange)/0.4)] bg-[hsl(var(--orange)/0.08)] px-3 py-1 text-xs font-semibold text-[hsl(var(--orange))]" data-testid="payments-test-badge">
            <FlaskConical className="h-3.5 w-3.5" /> TEST MODE — payments are simulated until Razorpay keys are added
          </div>
        )}
        <Tabs value={method} onValueChange={setMethod} className="mt-2">
          <TabsList className="grid grid-cols-2 md:grid-cols-5 gap-1 bg-[hsl(var(--surface-tint))] p-1">
            <TabsTrigger data-testid="donate-method-upi" value="upi"><Smartphone className="h-3.5 w-3.5 mr-1.5" /> UPI</TabsTrigger>
            <TabsTrigger data-testid="donate-method-bank_transfer" value="bank_transfer"><Landmark className="h-3.5 w-3.5 mr-1.5" /> Bank</TabsTrigger>
            <TabsTrigger data-testid="donate-method-credit_card" value="credit_card"><CreditCard className="h-3.5 w-3.5 mr-1.5" /> Credit</TabsTrigger>
            <TabsTrigger data-testid="donate-method-debit_card" value="debit_card"><CreditCard className="h-3.5 w-3.5 mr-1.5" /> Debit</TabsTrigger>
            <TabsTrigger data-testid="donate-method-net_banking" value="net_banking"><Banknote className="h-3.5 w-3.5 mr-1.5" /> Netbanking</TabsTrigger>
          </TabsList>

          {ONLINE_METHODS.map((m) => (
            <TabsContent key={m} value={m} className="mt-4 rounded-xl border border-[hsl(var(--border))] p-4">
              <div className="flex items-start gap-3 text-sm">
                <Lock className="h-4 w-4 mt-0.5 text-[hsl(var(--green))]" />
                <div>
                  <div className="font-semibold text-[hsl(var(--navy))]">Secure online payment via Razorpay</div>
                  <p className="text-[hsl(var(--muted-ink))] mt-1">Click <strong>Donate</strong> below — a secure {m === "upi" ? "UPI" : m.replace("_", " ")} checkout opens instantly. Your 80G receipt is generated the moment payment succeeds.</p>
                </div>
              </div>
            </TabsContent>
          ))}

          <TabsContent value="bank_transfer" className="mt-4 rounded-xl border border-[hsl(var(--border))] p-4">
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div><div className="eyebrow mb-1">Account name</div><div>Citizen Awareness Foundation</div></div>
              <div><div className="eyebrow mb-1">Account number</div><div className="tabular">50100 22233 44455</div></div>
              <div><div className="eyebrow mb-1">IFSC</div><div>HDFC0000123</div></div>
              <div><div className="eyebrow mb-1">Bank / Branch</div><div>HDFC Bank, MG Road, Bengaluru</div></div>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-[hsl(var(--muted-ink))]">
              <button type="button" onClick={() => { navigator.clipboard.writeText("50100 22233 44455 / HDFC0000123"); toast.success("Bank details copied"); }} className="h-7 px-2 rounded-md border border-[hsl(var(--border))] inline-flex items-center gap-1"><Copy className="h-3 w-3" /> Copy details</button>
              Submit the form to record your donation, then transfer at your convenience.
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <div>
        <Field label="Message for our Donor Wall (optional)"><Textarea data-testid="donate-note" rows={2} value={form.note} onChange={set("note")} placeholder="A few words of hope — shown publicly next to your donation" /></Field>
      </div>

      <div className="flex items-center gap-2"><Checkbox data-testid="donate-anonymous" checked={form.anonymous} onCheckedChange={(v) => set("anonymous")(!!v)} id="anon" /><Label htmlFor="anon" className="text-sm text-[hsl(var(--muted-ink))]">Keep my donation anonymous on the Donor Wall and public reports</Label></div>

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-ink))]"><ShieldCheck className="h-4 w-4 text-[hsl(var(--green))]" />100% Transparent • 80G eligible • Audited annually</div>
        <Button disabled={loading} type="submit" data-testid="donate-submit" className="bg-[hsl(var(--orange))] hover:bg-[hsl(var(--orange-2))] text-black font-semibold">{loading ? "Processing..." : `Donate ₹${Number(form.amount || 0).toLocaleString("en-IN")}`}</Button>
      </div>
    </form>

    {/* TEST MODE payment simulator — mirrors the live Razorpay flow */}
    <Dialog open={!!simulator} onOpenChange={(o) => !o && setSimulator(null)}>
      <DialogContent data-testid="payment-simulator-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FlaskConical className="h-5 w-5 text-[hsl(var(--orange))]" /> Test Payment Simulator</DialogTitle>
          <DialogDescription>
            Razorpay keys are not configured yet, so this simulates the checkout. The rest of the flow — verification, receipt, donor wall — is fully real.
          </DialogDescription>
        </DialogHeader>
        {simulator && (
          <div className="rounded-lg border border-[hsl(var(--border))] p-4 text-sm space-y-1.5">
            <div className="flex justify-between"><span className="text-[hsl(var(--muted-ink))]">Amount</span><strong className="tabular">₹{(simulator.order.amount / 100).toLocaleString("en-IN")}</strong></div>
            <div className="flex justify-between"><span className="text-[hsl(var(--muted-ink))]">Order</span><code className="text-xs">{simulator.order.order_id}</code></div>
            <div className="flex justify-between"><span className="text-[hsl(var(--muted-ink))]">Method</span><span className="uppercase">{method.replace("_", " ")}</span></div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" data-testid="simulator-cancel" onClick={() => { setSimulator(null); toast.info("Payment cancelled."); }}>Cancel</Button>
          <Button
            data-testid="simulator-pay-button"
            className="bg-[hsl(var(--green))] hover:bg-[hsl(var(--green-2))] text-white font-semibold"
            onClick={() => {
              const sim = simulator;
              setSimulator(null);
              finishPayment(sim.donation.id, sim.order.order_id, `pay_test_${Date.now()}`, null);
            }}
          >
            Simulate Successful Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm font-medium text-[hsl(var(--navy))]">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
